import Pusher from "pusher-js";
import Vue from "vue";
import axios from "../axios";

export const notificationBus = new Vue();

const PUSHER_KEY = "b89af738d362d3ca27d2";
const PUSHER_CLUSTER = "ap2";

let pusherInstance = null;
let initializedForType = null;
let recentEventKeys = {};

function getPayloadId(payload, userType) {
  if (userType === "super_admin") {
    return (
      payload?.notification?.id ||
      payload?.notification_id ||
      payload?.id ||
      null
    );
  }

  return (
    payload?.notification?.id ||
    payload?.recipient_record_id ||
    payload?.id ||
    payload?.notification_id ||
    null
  );
}

function isDuplicateRealtimeEvent(payload, userType) {
  const id = getPayloadId(payload, userType);
  const key = [
    payload?.action || "unknown",
    userType,
    id || payload?.message || payload?.title || "none",
  ].join(":");

  const now = Date.now();
  if (recentEventKeys[key] && now - recentEventKeys[key] < 2000) {
    return true;
  }

  recentEventKeys[key] = now;

  Object.keys(recentEventKeys).forEach((eventKey) => {
    if (now - recentEventKeys[eventKey] > 5000) {
      delete recentEventKeys[eventKey];
    }
  });

  return false;
}

export function normalizeUnreadNotification(item, userType) {
  if (userType === "super_admin") {
    return {
      ...item,
      id: item.id,
      timestamp: item.created_at || new Date().toISOString(),
      message: item.message || "No message",
      title: item.title || "",
      viewed: item.viewed || "false",
      vehicle_id: item.vehicle_id,
    };
  }

  return {
    ...item,
    id: item.id,
    notification_id: item.notification_id,
    timestamp: item.created_at || new Date().toISOString(),
    message: item.notification?.message || item.message || "No message",
    title: item.notification?.title || item.title || "",
    viewed: item.viewed || "false",
    vehicle_id: item.notification?.vehicle_id,
  };
}

function notificationExists(list, id) {
  if (id === null || id === undefined || id === "") {
    return false;
  }

  return list.some((item) => String(item.id) === String(id));
}

export function applyNotificationSync(notifications, unreadCount, payload, userType) {
  let nextNotifications = Array.isArray(notifications) ? [...notifications] : [];
  let nextCount =
    typeof payload.unread_count === "number" ? payload.unread_count : unreadCount;

  if (
    payload.user_type === "recipient" &&
    payload.user_id &&
    String(payload.user_id) !== String(localStorage.getItem("userId"))
  ) {
    return { notifications: nextNotifications, unreadCount: nextCount };
  }

  if (isDuplicateRealtimeEvent(payload, userType)) {
    return { notifications: nextNotifications, unreadCount: nextCount };
  }

  switch (payload.action) {
    case "created": {
      if (payload.notification) {
        const normalized = normalizeUnreadNotification(payload.notification, userType);
        if (!notificationExists(nextNotifications, normalized.id)) {
          nextNotifications.unshift(normalized);
        }
      } else if (payload.title || payload.message) {
        const id = getPayloadId(payload, userType);
        if (!notificationExists(nextNotifications, id)) {
          nextNotifications.unshift({
            id,
            title: payload.title || "Notification",
            message: payload.message || "",
            timestamp: new Date().toISOString(),
            viewed: "false",
            vehicle_id: payload.vehicle_id,
          });
        }
      }
      break;
    }

    case "deleted":
      if (userType === "super_admin") {
        nextNotifications = nextNotifications.filter(
          (item) =>
            String(item.id) !== String(payload.notification_id) &&
            String(item.id) !== String(payload.id)
        );
      } else {
        nextNotifications = nextNotifications.filter(
          (item) =>
            String(item.id) !== String(payload.recipient_record_id) &&
            String(item.id) !== String(payload.id) &&
            String(item.notification_id) !== String(payload.notification_id)
        );
      }
      break;

    case "bulk_deleted":
      nextNotifications = [];
      nextCount = 0;
      break;

    case "viewed":
      if (userType === "super_admin") {
        nextNotifications = nextNotifications.filter(
          (item) =>
            String(item.id) !== String(payload.notification_id) &&
            String(item.id) !== String(payload.id)
        );
      } else {
        nextNotifications = nextNotifications.filter(
          (item) =>
            String(item.id) !== String(payload.recipient_record_id) &&
            String(item.id) !== String(payload.id)
        );
      }
      if (typeof payload.unread_count !== "number") {
        nextCount = Math.max(0, nextCount - 1);
      }
      if (nextCount === 0) {
        nextNotifications = [];
      }
      break;

    case "all_viewed":
      nextNotifications = [];
      nextCount = 0;
      break;

    default:
      break;
  }

  return { notifications: nextNotifications, unreadCount: nextCount };
}

function bindSyncHandler(channel, userType) {
  channel.bind("notification-sync", (payload) => {
    notificationBus.$emit("sync", { payload, userType });
  });
}

export async function initNotificationRealtime(userType) {
  if (userType !== "super_admin" && userType !== "police") {
    return;
  }

  if (initializedForType === userType && pusherInstance) {
    return;
  }

  initializedForType = userType;

  let config = null;
  try {
    const response = await axios.get("notificationRealtimeConfig");
    if (response.data?.success) {
      config = response.data.data;
    }
  } catch (error) {
    console.warn("notificationRealtimeConfig unavailable, using defaults", error);
  }

  const pusherKey = config?.pusher_key || PUSHER_KEY;
  const cluster = config?.cluster || PUSHER_CLUSTER;
  const userId = localStorage.getItem("userId");

  if (pusherInstance) {
    pusherInstance.disconnect();
  }

  pusherInstance = new Pusher(pusherKey, {
    cluster,
    encrypted: true,
  });

  const channelName =
    config?.channel ||
    (userType === "super_admin"
      ? "super-admin-channel"
      : `user-notifications-${userId}`);

  const mainChannel = pusherInstance.subscribe(channelName);
  bindSyncHandler(mainChannel, userType);

  if (userType === "super_admin") {
    const recoveredChannel = pusherInstance.subscribe("super-admin-channel-recovered");
    recoveredChannel.bind("admin-notification-recovered", (data) => {
      notificationBus.$emit("sync", {
        payload: {
          ...data,
          action: "created",
          user_type: "admin",
          title: data.title || "Vehicle Recovered",
          message: data.message || "Vehicle recovered",
        },
        userType,
      });
    });
  }
}

export function applyListSync(users, total, payload, userType) {
  let nextUsers = Array.isArray(users) ? [...users] : [];
  let nextTotal = typeof total === "number" ? total : 0;

  if (
    payload.user_type === "recipient" &&
    payload.user_id &&
    String(payload.user_id) !== String(localStorage.getItem("userId"))
  ) {
    return { users: nextUsers, total: nextTotal, shouldRefetch: false };
  }

  switch (payload.action) {
    case "created":
      return { users: nextUsers, total: nextTotal, shouldRefetch: true };

    case "deleted":
      if (userType === "super_admin") {
        nextUsers = nextUsers.filter(
          (item) =>
            String(item.id) !== String(payload.notification_id) &&
            String(item.id) !== String(payload.id)
        );
      } else {
        nextUsers = nextUsers.filter(
          (item) =>
            String(item.id) !== String(payload.recipient_record_id) &&
            String(item.id) !== String(payload.id)
        );
      }
      nextTotal = Math.max(0, nextTotal - 1);
      break;

    case "bulk_deleted":
      nextUsers = [];
      nextTotal = 0;
      break;

    default:
      break;
  }

  return { users: nextUsers, total: nextTotal, shouldRefetch: false };
}
